import { useState, useEffect, useCallback, useMemo } from 'react';
import { collection, getDocs, query, orderBy } from 'firebase/firestore';
import { db } from '../../firebase';
import PageHeader from '../../components/PageHeader';
import ToastNotification from '../../components/ToastNotification';
import styles from './ArchivesPage.module.css';
import { formatOrderId, formatTransactionId } from '../../utils/formatOrderId';

const PAGE_SIZE = 10;
const TABS = ['Orders', 'Transactions', 'Reports'];

function formatDate(ts) {
  if (!ts) return '—';
  const d = ts.toDate ? ts.toDate() : new Date(ts);
  return d.toISOString().slice(0, 10);
}

function exportToXML(data, filename) {
  if (!data.length) return;
  const headers = Object.keys(data[0]);
  const rows = data.map(row =>
    `  <Row>${headers.map(h => `<Cell><Data ss:Type="String">${String(row[h] ?? '').replace(/&/g,'&amp;').replace(/</g,'&lt;').replace(/>/g,'&gt;')}</Data></Cell>`).join('')}</Row>`
  ).join('\n');
  const xml = `<?xml version="1.0"?>\n<?mso-application progid="Excel.Sheet"?>\n<Workbook xmlns="urn:schemas-microsoft-com:office:spreadsheet" xmlns:ss="urn:schemas-microsoft-com:office:spreadsheet">\n<Worksheet ss:Name="Sheet1"><Table>\n  <Row>${headers.map(h => `<Cell><Data ss:Type="String">${h}</Data></Cell>`).join('')}</Row>\n${rows}\n</Table></Worksheet></Workbook>`;
  const blob = new Blob([xml], { type: 'application/vnd.ms-excel' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url; a.download = filename; a.click();
  URL.revokeObjectURL(url);
}

function inDateRange(ts, from, to) {
  if (!from && !to) return true;
  const d = ts ? (ts.toDate ? ts.toDate() : new Date(ts)) : null;
  if (!d) return true;
  const dateStr = d.toISOString().slice(0, 10);
  if (from && dateStr < from) return false;
  if (to   && dateStr > to)   return false;
  return true;
}

function StatusBadge({ status }) {
  const key = status.replace(/\s+/g, '_');
  return <span className={`${styles.badge} ${styles[`badge_${key}`]}`}>{status}</span>;
}

function DateBranchFilters({ fromDate, toDate, branch, branches, onFromDate, onToDate, onBranch }) {
  return (
    <div className={styles.filterBar}>
      <div className={styles.dateGroup}>
        <label className={styles.filterLabel}>From</label>
        <input type="date" className={styles.dateInput} value={fromDate} onChange={e => onFromDate(e.target.value)} />
      </div>
      <div className={styles.dateGroup}>
        <label className={styles.filterLabel}>To</label>
        <input type="date" className={styles.dateInput} value={toDate} onChange={e => onToDate(e.target.value)} />
      </div>
      <div className={styles.dateGroup}>
        <label className={styles.filterLabel}>Branch</label>
        <select className={styles.branchSelect} value={branch} onChange={e => onBranch(e.target.value)}>
          <option value="">All Branches</option>
          {branches.map(b => <option key={b} value={b}>{b}</option>)}
        </select>
      </div>
    </div>
  );
}

function Pagination({ page, totalPages, onPrev, onNext }) {
  return (
    <div className={styles.pagination}>
      <button className={styles.pageBtn} onClick={onPrev} disabled={page === 1}>← Previous</button>
      <span className={styles.pageIndicator}>Page {page} of {totalPages}</span>
      <button className={styles.pageBtn} onClick={onNext} disabled={page === totalPages}>Next →</button>
    </div>
  );
}

function OrdersTab({ orders, onToast }) {
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate]     = useState('');
  const [branch, setBranch]     = useState('');
  const [page, setPage]         = useState(1);

  const branches = useMemo(() => [...new Set(orders.map(o => o.branch).filter(Boolean))], [orders]);

  const filtered = useMemo(() =>
    orders.filter(o =>
      inDateRange(o.createdAt, fromDate, toDate) &&
      (!branch || o.branch === branch)
    ), [orders, fromDate, toDate, branch]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const safePage   = Math.min(page, totalPages);
  const pageRows   = filtered.slice((safePage - 1) * PAGE_SIZE, safePage * PAGE_SIZE);

  const flat = filtered.map(o => ({
    id: o._docId, branch: o.branch, details: o.details || '', total: o.total,
    date: formatDate(o.createdAt), status: o.status,
  }));

  return (
    <>
      <div className={styles.tabToolbar}>
        <DateBranchFilters fromDate={fromDate} toDate={toDate} branch={branch} branches={branches}
          onFromDate={v => { setFromDate(v); setPage(1); }} onToDate={v => { setToDate(v); setPage(1); }} onBranch={v => { setBranch(v); setPage(1); }} />
        <div className={styles.actionBtns}>
          <button className={styles.exportBtn} onClick={() => { const f = `orders-archive-${new Date().toISOString().slice(0,10)}.xml`; exportToXML(flat, f); onToast(`Exported ${f}`); }}>Export</button>
        </div>
      </div>
      <div className={`${styles.tableWrap} ${styles.printArea}`}>
        <div className={styles.printHeader}>Top Juan Franchising Inc. — Orders Archive — {new Date().toLocaleDateString()}</div>
        <table className={styles.table}>
          <thead><tr><th>Order ID</th><th>Branch</th><th>Items</th><th>Total</th><th>Date</th><th>Status</th></tr></thead>
          <tbody>
            {pageRows.length === 0 ? <tr><td colSpan={6} className={styles.empty}>No records match the selected filters.</td></tr> : (
              pageRows.map((o, i) => (
                <tr key={o._docId} className={i % 2 === 0 ? styles.rowEven : styles.rowOdd}>
                  <td className={styles.recordId}>{formatOrderId(o._docId)}</td>
                  <td>{o.branch}</td>
                  <td className={styles.muted}>{o.details || (o.items || []).map(x => `${x.productId} (${x.qty}x)`).join(', ')}</td>
                  <td className={styles.amount}>{typeof o.total === 'number' ? `₱${o.total.toLocaleString()}` : o.total}</td>
                  <td>{formatDate(o.createdAt)}</td>
                  <td><StatusBadge status={o.status} /></td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
      <Pagination page={safePage} totalPages={totalPages} onPrev={() => setPage(p => Math.max(1, p - 1))} onNext={() => setPage(p => Math.min(totalPages, p + 1))} />
    </>
  );
}

function TransactionsTab({ transactions, onToast }) {
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate]     = useState('');
  const [branch, setBranch]     = useState('');
  const [page, setPage]         = useState(1);

  const branches = useMemo(() => [...new Set(transactions.map(t => t.branch).filter(Boolean))], [transactions]);

  const filtered = useMemo(() =>
    transactions.filter(t =>
      inDateRange(t.createdAt, fromDate, toDate) &&
      (!branch || t.branch === branch)
    ), [transactions, fromDate, toDate, branch]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const safePage   = Math.min(page, totalPages);
  const pageRows   = filtered.slice((safePage - 1) * PAGE_SIZE, safePage * PAGE_SIZE);

  const flat = filtered.map(t => ({
    id: t._docId, orderId: t.orderId, branch: t.branch,
    amount: t.amount, type: t.type, date: formatDate(t.createdAt), status: t.status,
  }));

  return (
    <>
      <div className={styles.tabToolbar}>
        <DateBranchFilters fromDate={fromDate} toDate={toDate} branch={branch} branches={branches}
          onFromDate={v => { setFromDate(v); setPage(1); }} onToDate={v => { setToDate(v); setPage(1); }} onBranch={v => { setBranch(v); setPage(1); }} />
        <div className={styles.actionBtns}>
          <button className={styles.exportBtn} onClick={() => { const f = `transactions-archive-${new Date().toISOString().slice(0,10)}.xml`; exportToXML(flat, f); onToast(`Exported ${f}`); }}>Export</button>
        </div>
      </div>
      <div className={`${styles.tableWrap} ${styles.printArea}`}>
        <div className={styles.printHeader}>Top Juan Franchising Inc. — Transactions Archive — {new Date().toLocaleDateString()}</div>
        <table className={styles.table}>
          <thead><tr><th>Transaction ID</th><th>Order ID</th><th>Branch</th><th>Amount</th><th>Type</th><th>Date</th><th>Status</th></tr></thead>
          <tbody>
            {pageRows.length === 0 ? <tr><td colSpan={7} className={styles.empty}>No records match the selected filters.</td></tr> : (
              pageRows.map((t, i) => (
                <tr key={t._docId} className={i % 2 === 0 ? styles.rowEven : styles.rowOdd}>
                  <td className={styles.recordId}>{formatTransactionId(t._docId, t.createdAt)}</td>
                  <td className={styles.muted}>{formatOrderId(t.orderId)}</td>
                  <td>{t.branch}</td>
                  <td className={styles.amount}>{typeof t.amount === 'number' ? `₱${t.amount.toLocaleString()}` : t.amount}</td>
                  <td><span className={`${styles.badge} ${styles['badge_' + (t.type || 'Cash').replace(/\s+/g, '_')]}`}>{t.type}</span></td>
                  <td>{formatDate(t.createdAt)}</td>
                  <td><StatusBadge status={t.status} /></td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
      <Pagination page={safePage} totalPages={totalPages} onPrev={() => setPage(p => Math.max(1, p - 1))} onNext={() => setPage(p => Math.min(totalPages, p + 1))} />
    </>
  );
}

function php(n) { return `₱${(n || 0).toLocaleString()}`; }

function ratingFromOrders(count) {
  if (count >= 10) return 'High';
  if (count >= 5)  return 'Medium';
  return 'Low';
}

function RatingBadge({ rating }) {
  const cls = rating === 'High' ? styles.ratingHigh : rating === 'Medium' ? styles.ratingMedium : styles.ratingLow;
  return <span className={`${styles.badge} ${cls}`}>{rating}</span>;
}

function formatWeekLabel(mondayStr) {
  if (!mondayStr) return '—';
  const start = new Date(mondayStr + 'T00:00:00');
  const end   = new Date(mondayStr + 'T00:00:00');
  end.setDate(end.getDate() + 6);
  const fmt = d => d.toLocaleDateString('en-PH', { month: 'short', day: 'numeric' });
  return `${fmt(start)} – ${fmt(end)}, ${end.getFullYear()}`;
}

const REPORT_SUBTABS = ['Branch Performance', 'System Summary', 'Weekly Profits'];

function ReportsTab({ orders, transactions, weeklyRecords, onToast }) {
  const [sub, setSub] = useState('Branch Performance');

  const branchPerf = useMemo(() => {
    const map = {};
    for (const o of orders) {
      const name = o.branch ?? o.branchId ?? 'Unknown';
      if (!map[name]) map[name] = { branch: name, orders: 0, total: 0 };
      map[name].orders += 1;
      map[name].total  += typeof o.total === 'number' ? o.total : 0;
    }
    return Object.values(map)
      .sort((a, b) => b.total - a.total)
      .map(b => ({ ...b, avgOrder: b.orders > 0 ? Math.round(b.total / b.orders) : 0, rating: ratingFromOrders(b.orders) }));
  }, [orders]);

  const systemStats = useMemo(() => {
    const totalRevenue  = orders.reduce((s, o) => s + (typeof o.total === 'number' ? o.total : 0), 0);
    const pendingOrders = orders.filter(o => o.status === 'Pending').length;
    const activeBranches = new Set(orders.map(o => o.branchId)).size;
    const avgOrderValue  = orders.length > 0 ? Math.round(totalRevenue / orders.length) : 0;
    const txVerified     = transactions.filter(t => t.status === 'Verified').length;
    return [
      { label: 'Total Orders',          value: orders.length },
      { label: 'Total Revenue',         value: `₱${totalRevenue.toLocaleString()}` },
      { label: 'Active Branches',       value: activeBranches },
      { label: 'Pending Orders',        value: pendingOrders },
      { label: 'Avg Order Value',       value: `₱${avgOrderValue.toLocaleString()}` },
      { label: 'Transactions Verified', value: txVerified },
    ];
  }, [orders, transactions]);

  const weeklySummary = useMemo(() => {
    const map = {};
    for (const r of weeklyRecords) {
      const key = r.weekStart;
      if (!map[key]) map[key] = { weekStart: key, weekLabel: r.weekLabel || formatWeekLabel(key), revenue: 0, expense: 0, net: 0, branches: 0 };
      map[key].revenue  += r.weeklyRevenue || 0;
      map[key].expense  += r.supplyExpense  || 0;
      map[key].net      += r.netProfit      || 0;
      map[key].branches += 1;
    }
    return Object.values(map).sort((a, b) => b.weekStart.localeCompare(a.weekStart));
  }, [weeklyRecords]);

  function exportBranchPerf() {
    const flat = branchPerf.map((b, i) => ({ rank: i + 1, branch: b.branch, orders: b.orders, revenue: b.total, avgOrder: b.avgOrder, rating: b.rating }));
    exportToXML(flat, `branch-performance-${new Date().toISOString().slice(0,10)}.xml`);
    onToast('Exported branch performance');
  }

  function exportSystemSummary() {
    const flat = systemStats.map(s => ({ metric: s.label, value: String(s.value) }));
    exportToXML(flat, `system-summary-${new Date().toISOString().slice(0,10)}.xml`);
    onToast('Exported system summary');
  }

  function exportWeeklyProfits() {
    const flat = weeklyRecords.map(r => ({
      week: r.weekLabel || formatWeekLabel(r.weekStart),
      branch: r.branchName || '—',
      weeklyRevenue: r.weeklyRevenue || 0,
      supplyExpense: r.supplyExpense  || 0,
      netProfit:     r.netProfit      || 0,
    }));
    exportToXML(flat, `weekly-profits-${new Date().toISOString().slice(0,10)}.xml`);
    onToast('Exported weekly profits');
  }

  return (
    <>
      {/* Sub-tab row */}
      <div className={styles.subTabRow}>
        {REPORT_SUBTABS.map(t => (
          <button key={t} className={`${styles.subTab} ${sub === t ? styles.subTabActive : ''}`} onClick={() => setSub(t)}>{t}</button>
        ))}
      </div>

      {sub === 'Branch Performance' && (
        <>
          <div className={styles.tabToolbar}>
            <div />
            <div className={styles.actionBtns}>
              <button className={styles.exportBtn} onClick={exportBranchPerf}>Export</button>
            </div>
          </div>
          <div className={styles.tableWrap}>
            <table className={styles.table}>
              <thead><tr><th>Rank</th><th>Branch</th><th>Orders</th><th>Total Revenue</th><th>Avg Order Value</th><th>Rating</th></tr></thead>
              <tbody>
                {branchPerf.length === 0
                  ? <tr><td colSpan={6} className={styles.empty}>No order data yet.</td></tr>
                  : branchPerf.map((b, i) => (
                    <tr key={b.branch} className={i % 2 === 0 ? styles.rowEven : styles.rowOdd}>
                      <td className={styles.recordId}>{i + 1}</td>
                      <td>{b.branch}</td>
                      <td>{b.orders}</td>
                      <td className={styles.amount}>₱{b.total.toLocaleString()}</td>
                      <td>₱{b.avgOrder.toLocaleString()}</td>
                      <td><RatingBadge rating={b.rating} /></td>
                    </tr>
                  ))}
              </tbody>
            </table>
          </div>
          <p className={styles.perfNote}>Branches with Low rating may need supply chain support.</p>
        </>
      )}

      {sub === 'System Summary' && (
        <>
          <div className={styles.tabToolbar}>
            <div />
            <div className={styles.actionBtns}>
              <button className={styles.exportBtn} onClick={exportSystemSummary}>Export</button>
            </div>
          </div>
          <div className={styles.statsGrid}>
            {systemStats.map(stat => (
              <div key={stat.label} className={styles.statCard}>
                <span className={styles.statValue}>{stat.value}</span>
                <span className={styles.statLabel}>{stat.label}</span>
              </div>
            ))}
          </div>
        </>
      )}

      {sub === 'Weekly Profits' && (
        <>
          <div className={styles.tabToolbar}>
            <div />
            <div className={styles.actionBtns}>
              <button className={styles.exportBtn} onClick={exportWeeklyProfits}>Export</button>
            </div>
          </div>
          {/* Weekly summary */}
          <div className={styles.tableWrap}>
            <table className={styles.table}>
              <thead>
                <tr><th>Week</th><th>Branches Reported</th><th>Weekly Order Expense</th><th>Weekly Revenue</th><th>Weekly Net Profit</th></tr>
              </thead>
              <tbody>
                {weeklySummary.length === 0
                  ? <tr><td colSpan={5} className={styles.empty}>No weekly profit records yet.</td></tr>
                  : weeklySummary.map((w, i) => (
                    <tr key={w.weekStart} className={i % 2 === 0 ? styles.rowEven : styles.rowOdd}>
                      <td>{w.weekLabel}</td>
                      <td style={{ textAlign: 'center' }}>{w.branches}</td>
                      <td className={styles.amount}>{php(w.expense)}</td>
                      <td className={styles.amount}>{php(w.revenue)}</td>
                      <td className={styles.amount}>{php(w.net)}</td>
                    </tr>
                  ))}
              </tbody>
            </table>
          </div>
          {/* Branch breakdown */}
          <div className={styles.tableWrap} style={{ marginTop: '1.5rem' }}>
            <table className={styles.table}>
              <thead>
                <tr><th>Week</th><th>Branch</th><th>Weekly Revenue</th><th>Supply Order Expense</th><th>Net Profit</th></tr>
              </thead>
              <tbody>
                {weeklyRecords.length === 0
                  ? <tr><td colSpan={5} className={styles.empty}>No records yet.</td></tr>
                  : weeklyRecords.map((r, i) => (
                    <tr key={r.id} className={i % 2 === 0 ? styles.rowEven : styles.rowOdd}>
                      <td>{r.weekLabel || formatWeekLabel(r.weekStart)}</td>
                      <td>{r.branchName || '—'}</td>
                      <td className={styles.amount}>{php(r.weeklyRevenue)}</td>
                      <td className={styles.amount}>{php(r.supplyExpense)}</td>
                      <td className={styles.amount}>{php(r.netProfit)}</td>
                    </tr>
                  ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </>
  );
}

export default function ArchivesPage() {
  const [activeTab, setActiveTab]         = useState('Orders');
  const [orders, setOrders]               = useState([]);
  const [transactions, setTransactions]   = useState([]);
  const [weeklyRecords, setWeeklyRecords] = useState([]);
  const [toast, setToast]                 = useState(null);
  const dismissToast                      = useCallback(() => setToast(null), []);

  useEffect(() => {
    async function load() {
      try {
        const wpQuery = query(collection(db, 'weeklyProfits'), orderBy('weekStart', 'desc'));
        const [ordSnap, txSnap, wpSnap] = await Promise.all([
          getDocs(collection(db, 'orders')),
          getDocs(collection(db, 'transactions')),
          getDocs(wpQuery),
        ]);
        setOrders(ordSnap.docs.map(d => ({ _docId: d.id, ...d.data() })));
        setTransactions(txSnap.docs.map(d => ({ _docId: d.id, ...d.data() })));
        setWeeklyRecords(wpSnap.docs.map(d => ({ id: d.id, ...d.data() })));
      } catch (e) {
        console.error('ArchivesPage load error:', e);
      }
    }
    load();
  }, []);

  return (
    <div className={styles.page}>
      <PageHeader title="Archives" description="Transaction and order history" />

      <div className={styles.tabRow} aria-label="Archive tabs">
        {TABS.map(tab => (
          <button key={tab} className={`${styles.tab} ${activeTab === tab ? styles.tabActive : ''}`} onClick={() => setActiveTab(tab)}>{tab}</button>
        ))}
      </div>

      <div className={styles.tabContent}>
        {activeTab === 'Orders'       && <OrdersTab       orders={orders}           onToast={m => setToast({ message: m, type: 'success' })} />}
        {activeTab === 'Transactions' && <TransactionsTab transactions={transactions} onToast={m => setToast({ message: m, type: 'success' })} />}
        {activeTab === 'Reports'      && <ReportsTab orders={orders} transactions={transactions} weeklyRecords={weeklyRecords} onToast={m => setToast({ message: m, type: 'success' })} />}
      </div>

      {toast && <ToastNotification message={toast.message} type={toast.type} onDismiss={dismissToast} />}
    </div>
  );
}
